import { useMemo } from 'react';

import { Button } from '@components/button';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { InfoBox } from '@components/info-box';
import { LabelTextGroup } from '@components/label-text-group';
import { ShowMore } from '@components/show-more';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { TravellerJourneyActionDetailed } from 'api/types';

import { hasErrorResponse, ErrorResponseSection } from './error-response-section';
import { storedDataEntries } from './stored-data-entries';
import { sidebarLocale } from './traveller-sidebar-locale';

import { ActionProgressBar, isActionError, useEditingContext } from '../common';

const RESPONSE_ROW_LIMIT = 50;

type Props = { action: TravellerJourneyActionDetailed<'integration'> };

export const DeletedEndpointDetailsContent = ({ action }: Props) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { isEditing, stopEditing } = useEditingContext();
  const requestEntries = useMemo(() => storedDataEntries(action.data?.requestData), [action.data]);
  const responseEntries = useMemo(
    () => storedDataEntries(action.data?.responseData),
    [action.data],
  );
  const statusCode: unknown = action.data?.statusCode;
  const hasStatusCode = typeof statusCode === 'number';
  const hasError = hasErrorResponse(action);
  const hasRecordedRun =
    requestEntries.length > 0 || responseEntries.length > 0 || hasStatusCode || hasError;

  return (
    <Stack space={6}>
      <InfoBox
        variant="orange"
        icon={<IconFormIndicatorWarning />}
        text={hasRecordedRun ? t('integrationDeleted') : t('integrationDeletedNotRun')}
      />
      <ActionProgressBar action={action} isError={isActionError(action)} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <LabelTextGroup label={t('description')} text={action.description} />
      </HideContainer>
      <ErrorResponseSection action={action} />
      {requestEntries.length > 0 && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('requestData')}>
            <Stack space={4}>
              {requestEntries.map((entry) => (
                <LabelTextGroup key={entry.key} label={entry.label} text={entry.text} />
              ))}
            </Stack>
          </HideContainer>
        </>
      )}
      {(hasStatusCode || responseEntries.length > 0) && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('responseData')}>
            <Stack space={4}>
              {hasStatusCode && (
                <LabelTextGroup label={t('statusCode')} text={String(statusCode)} />
              )}
              <ShowMore
                items={responseEntries}
                initialCount={RESPONSE_ROW_LIMIT}
                increment={responseEntries.length}
                renderItem={(entry) => (
                  <LabelTextGroup
                    key={entry.key}
                    label={entry.label}
                    text={
                      entry.key === 'file' && typeof entry.value === 'number'
                        ? t('fileUnavailable')
                        : entry.text
                    }
                  />
                )}
              />
            </Stack>
          </HideContainer>
        </>
      )}
      {isEditing && (
        <Sidebar.Footer>
          <Stack orientation="horizontal" justify="flex-end">
            <Button variant="secondary" text={t('core.cancel')} onClick={stopEditing} />
          </Stack>
        </Sidebar.Footer>
      )}
    </Stack>
  );
};
